import { useMemo, useState } from "react";
import { MapPin } from "lucide-react";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { useContent } from "@/hooks/useContent";
import { formatDate } from "@/lib/content";

const EventsList = () => {
  const { data: events } = useContent("/content/events.json", []);
  const [tab, setTab] = useState("upcoming");

  const { upcoming, past } = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const up = [];
    const pa = [];
    events.forEach((ev) => {
      if (!ev.date || new Date(`${ev.date}T00:00:00`) >= now) up.push(ev);
      else pa.push(ev);
    });
    return { upcoming: up, past: pa };
  }, [events]);

  const list = tab === "upcoming" ? upcoming : past;

  return (
    <section id="agenda" data-testid="events-section" className="border-y border-[#232638] bg-[#0B0C11]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <SectionHeading
          chapter="04 // Agenda de eventos"
          title="Próximos bailes"
          note="Bailes charme, festas funk 90s e club gigs — agenda atualizada no arquivo de eventos."
        />
        <div className="flex gap-2 mb-10">
          <button
            data-testid="event-filter-upcoming"
            onClick={() => setTab("upcoming")}
            className={`font-mono text-xs uppercase tracking-[0.2em] px-5 py-2.5 rounded-full border transition-colors duration-300 ${
              tab === "upcoming" ? "bg-amber-400 text-[#08090C] border-amber-400 font-bold" : "border-[#3F4462] text-zinc-400 hover:border-amber-400/60"
            }`}
          >
            Próximos ({upcoming.length})
          </button>
          <button
            data-testid="event-filter-past"
            onClick={() => setTab("past")}
            className={`font-mono text-xs uppercase tracking-[0.2em] px-5 py-2.5 rounded-full border transition-colors duration-300 ${
              tab === "past" ? "bg-amber-400 text-[#08090C] border-amber-400 font-bold" : "border-[#3F4462] text-zinc-400 hover:border-amber-400/60"
            }`}
          >
            Realizados ({past.length})
          </button>
        </div>

        <div className="flex flex-col gap-4">
          {list.length === 0 && (
            <p data-testid="events-empty-state" className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 py-10">
              Nenhum evento aqui por enquanto — volte em breve.
            </p>
          )}
          {list.map((ev, i) => {
            const d = formatDate(ev.date);
            return (
              <Reveal key={ev.id} delay={i * 0.07}>
                <article
                  data-testid={`event-card-${ev.id}`}
                  className="group grid grid-cols-[auto_1fr] sm:grid-cols-[auto_auto_1fr] items-center gap-5 sm:gap-8 border border-[#232638] hover:border-amber-400/50 bg-[#10121A] p-4 sm:p-6 transition-colors duration-500"
                >
                  <div className="w-16 sm:w-20 text-center border border-[#3F4462] bg-[#08090C] py-3">
                    {d ? (
                      <>
                        <p className="font-display text-2xl sm:text-3xl font-extrabold text-amber-400 leading-none">{d.day}</p>
                        <p className="font-mono text-[10px] tracking-[0.2em] text-zinc-400 mt-1">{d.month}</p>
                        <p className="font-mono text-[9px] text-zinc-600 mt-0.5">{d.year}</p>
                      </>
                    ) : (
                      <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-amber-400 leading-relaxed px-1">Em breve</p>
                    )}
                  </div>
                  <img
                    src={ev.image}
                    alt={ev.title}
                    loading="lazy"
                    className="hidden sm:block w-24 h-24 object-cover border border-[#232638]"
                  />
                  <div data-testid="event-card-details">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E11D48]">{ev.type}</span>
                    <h3 className="font-display text-lg sm:text-2xl font-bold uppercase tracking-tight mt-1 group-hover:text-amber-400 transition-colors duration-300">
                      {ev.title}
                    </h3>
                    <p className="mt-2 text-sm text-zinc-400 font-light max-w-2xl">{ev.description}</p>
                    <p className="mt-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                      <MapPin size={12} className="text-amber-400" /> {ev.venue} — {ev.city}
                    </p>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default EventsList;
